import React from 'react';
import { observer } from 'mobx-react';
import styles from './ReviewsTable.module.css';
import { Review } from '../../../models/reviews';
import { Button } from '../../../components/UI/Button/Button';

interface ReviewsTableProps {
  reviews: Review[];
  loading?: boolean;
  onEdit: (review: Review) => void;
  onDelete: (reviewId: string) => void;
  onToggleStatus: (reviewId: string, currentStatus: Review['status']) => void;
}

export const ReviewsTable: React.FC<ReviewsTableProps> = observer(
  ({ reviews, loading = false, onEdit, onDelete, onToggleStatus }) => {
    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const getStatusColor = (status: Review['status']) => {
      switch (status) {
        case 'published':
          return styles.statusPublished;
        case 'draft':
          return styles.statusDraft;
        case 'scheduled':
          return styles.statusScheduled;
        case 'hidden':
          return styles.statusHidden;
        default:
          return styles.statusDraft;
      }
    };

    const getStatusLabel = (status: Review['status']) => {
      switch (status) {
        case 'published':
          return 'Опубликован';
        case 'draft':
          return 'Черновик';
        case 'scheduled':
          return 'Запланирован';
        case 'hidden':
          return 'Скрыт';
        default:
          return 'Неизвестно';
      }
    };

    const getTranslation = (
      translations: Review['translations'],
      locale: 'ru' | 'en' = 'ru',
    ) => {
      return translations?.find((t) => t.locale === locale);
    };

    const getAuthorName = (author?: Review['author']) => {
      if (!author) {
        return 'Анонимный пользователь';
      }
      const fullName = `${author.first_name || ''} ${
        author.last_name || ''
      }`.trim();
      return fullName || 'Анонимный пользователь';
    };

    if (loading) {
      return (
        <div className={styles.container}>
          <div className={styles.loading}>
            <p>Загрузка отзывов...</p>
          </div>
        </div>
      );
    }

    if (!reviews || reviews.length === 0) {
      return (
        <div className={styles.container}>
          <div className={styles.empty}>
            <p>Отзывы не найдены</p>
            <p className={styles.emptyHint}>
              Измените фильтры или добавьте новый отзыв
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className={styles.container}>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Заголовок</th>
                <th>Короткое описание</th>
                <th>Автор отзыва</th>
                <th>Статус</th>
                <th>Дата создания</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {reviews.map((review) => {
                const ruTranslation = getTranslation(review.translations, 'ru');
                return (
                  <tr key={review.id}>
                    <td>
                      <div className={styles.title}>
                        {ruTranslation?.title || 'Без заголовка'}
                      </div>
                    </td>
                    <td>
                      <div className={styles.summary}>
                        {ruTranslation?.summary || 'Без описания'}
                      </div>
                    </td>
                    <td>
                      <div className={styles.author}>
                        {getAuthorName(review.author)}
                      </div>
                    </td>
                    <td>
                      <button
                        className={`${styles.status} ${getStatusColor(
                          review.status,
                        )}`}
                        onClick={() => onToggleStatus(review.id, review.status)}
                        disabled={loading}
                      >
                        {getStatusLabel(review.status)}
                      </button>
                    </td>
                    <td>
                      <div className={styles.date}>
                        {formatDate(review.created_at)}
                      </div>
                    </td>
                    <td>
                      <div className={styles.actions}>
                        <Button
                          appearance="link"
                          onClick={() => onEdit(review)}
                          disabled={loading}
                        >
                          Редактировать
                        </Button>
                        <Button
                          appearance="link"
                          onClick={() => onDelete(review.id)}
                          disabled={loading}
                          className={styles.deleteButton}
                        >
                          Удалить
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  },
);
